import { apiHeaders, axiosRetryGet, axiosRetryPost, axiosRetryPut, localApiInstance } from "../../../utilities/AxiosRetryUtil";
import { IDocumentSetCreationData } from "../interfaces/IDocumentSetCreationData";
import { IDownloadRequest } from "../interfaces/IDownloadRequest";
import { IFileSystemUploadData } from "../interfaces/IFileSystemUploadData";
import { IStoreItem } from "../interfaces/IStoredItem";
import { ITenantInfo } from "../interfaces/ITenantInfo";
import { IUserProfile, IUserSettings } from "../interfaces/IUserProfile";
import { IDocumentSearchRequest } from "../interfaces/advancedSearch/IDocumentSearchRequest";
import { IDocumentSearchResult } from "../interfaces/advancedSearch/IDocumentSearchResult";
import LZString = require("lz-string");
import { IOneDriveDataDto } from "../interfaces/IOneDriveDataDto";
import { IOneDriveOfficeDataDto } from "../interfaces/IOneDriveOfficeDataDto";
import { ISharepointDataDto } from "../interfaces/ISharepointDataDto";
import { ISharepointOfficeDataDto } from "../interfaces/ISharepointOfficeDataDto";
import { IDriveItemRequest } from "../interfaces/IDriveItemRequest";
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { IFilePreCommitRequest } from "../interfaces/IFilePreCommitRequest";
import { ApprovalRequest, BulkMetadataUpdateRequest, ConvertAndSaveAsPDFRequest, CreateDocumentFromTemplateRequest, ExchangeMailFolderUploadRequest, FolderPreCommitRequest, IHookRequest, IHookResult, MetadataUpdateRequest, ReplaceFileRequest, ScanTemplateCustomFieldsRequest, SignRequest, UpdateTriggerRequest } from "../components/_v2/redux/api/local/api-local-interfaces";
import { IAuthAccount, IAuthTokenResultDto, IJwtAuthTokenResult } from "../interfaces/IAuthTokenResultDto";
import { IFileSystemFolder } from "../../../utilities/DragAndDropHelper";
import { IMailSettingsDto } from "../../../utilities/OutlookUtils";
import { UserLicenseTypeEnum } from "../components/enums/userLicenseTypeEnum";
import { IFilePreCommitResult } from "../interfaces/IFilePreCommitResult";
import { ListItemCreationData } from "../interfaces/ListItemCreationData";
import { logger } from "../../../utilities/logger";
import { isAuthRejection, isInteractionRequired, ranOutOfTime, TOKEN_CALL_TIMEOUT_MS } from "../../../utilities/authRejection";
import authModule from "../../../utilities/AuthModule";
import { ITessAccount, ITessAccountsResult, ITessEmailRequest, ITessEmailTaskResponse, ITessProjectFolders } from "./TessService";
import { ICompleteFileUploadRequest, IFileUploadRequest } from "../interfaces/IFileUploadRequest";
import { IHixCompaniesResponse, IHixUploadTaskFilesRequest, IHixUploadTaskFilesResult, IHixUploadType } from "../interfaces/IHix";
import { ICopilotPrompt } from "../interfaces/ICopilotPrompt";
import { normalizeCopilotPrompts } from "./copilot/copilotPrompts";

const handleError = (error: any) => {
    if (error?.response?.status === 401 || error?.response?.status === 403) {
        console.error(error?.response?.data);
    } else {
        // Delegate to logAndRethrow so the module has ONE body-extraction policy: this
        // branch previously logged the raw response body, which is undefined/'' for
        // bodyless errors and lost the status/message logAndRethrow's fallback keeps.
        logAndRethrow(error, "ApiService.ts (handleError)");
    }
}

// Adds the call that failed to the label, "ApiService.ts (handleError GET User/GetPinnedItems/<id>)":
// handleError's label is shared by 53 calls and apiFileSystemCallDynamicEndpoint's by every save, the
// API's 500 body names no endpoint, and the caller's row that said which flow it was is no longer
// posted (the logger posts an error once). No query: it can carry e-mail addresses and codes.
const withCall = (context: string, error: any): string => {
    const method = String(error?.config?.method ?? "").toUpperCase();
    const path = String(error?.config?.url ?? "").split("?")[0].replace(process.env.API_URL ?? "", "");
    const call = [method, path].filter(Boolean).join(" ");
    if (!call) {
        return context;
    }
    return context.endsWith(")") ? `${context.slice(0, -1)} ${call})` : `${context} ${call}`;
}

// For save/upload mutations, ALWAYS log and rethrow — never route them through handleError,
// which swallows 401/403 and would let the caller treat an expired-token rejection as a
// successful save (silently losing data). The error itself is logged, so a caller logging it
// again posts nothing; the server's answer goes with it as its details (none when the body is
// absent or empty, leaving the status/message).
const logAndRethrow = (error: any, context: string): never => {
    logger(error, withCall(context, error), responseText(error?.response?.data));
    throw error;
}

// The server's answer as it was logged before: a body's message, or else the whole body.
const responseText = (data: any): string | undefined => {
    if (data === undefined || data === null || data === "") {
        return undefined;
    }
    if (typeof data === "string") {
        return data;
    }
    if (typeof data.message === "string" && data.message) {
        return data.message;
    }
    try {
        return JSON.stringify(data);
    } catch (error) {
        return undefined;
    }
}

export const refreshJwt = async (accessToken: string) => {
    try {
        // Use default axios retry instance to skip access token refresh interceptor. Every request's
        // interceptor waits for this one renewal (AuthModule.getDocubirdToken), and global axios sets
        // no time limit: one that never answered held every request (review 2026-10-20).
        const result: IJwtAuthTokenResult = (await axiosRetryGet(
            `${process.env.API_URL}auth/RefreshJwt`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    [apiHeaders.key.xDocubirdAuth]: apiHeaders.value.xDocubirdAuth
                },
                timeout: 10000
            }
        ))?.data;
        return result;
    } catch (error) {
        handleError(error);
    }
    return null;
}

export const apiSearchDocuments = async (documentSearchRequest: IDocumentSearchRequest) => {
    try {
        const results: IDocumentSearchResult[] = (await axiosRetryPost("Search/Documents", documentSearchRequest, undefined, true, localApiInstance))?.data;
        return results ?? [];
    } catch (error) {
        handleError(error);
    }
    return [];
}

export const apiGetTenantInfo = async (tenantId?: string) => {
    try {
        const results: ITenantInfo = (await axiosRetryGet(`tenants/${tenantId}`, undefined, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

// The admin-consent link for the caller's own tenant (phase 2a: GET api/Copilot/ConsentUrl). The
// API answers 401 without the account claim and 403 to a non-admin; a 404 comes only from an API
// older than this endpoint (the add-in deploys first). All expected, so they come back as null
// without a log entry; any other failure comes back as null too, logged. Only a Microsoft sign-in
// URL is returned: the caller opens it.
export const apiGetCopilotConsentUrl = async (): Promise<string> => {
    try {
        const url: string = (await axiosRetryGet("Copilot/ConsentUrl", undefined, localApiInstance))?.data;
        return typeof url === "string" && url.indexOf("https://login.microsoftonline.com/") === 0 ? url : null;
    } catch (error) {
        const status: number = error?.response?.status;
        if (status !== 401 && status !== 403 && status !== 404) {
            logger(error, "ApiService.ts (apiGetCopilotConsentUrl)");
        }
    }
    return null;
}

export const apiUpdateUserSettings = async (dbSettings: IUserSettings, accountId?: string) => {
    try {
        const results = (await axiosRetryPost(`User/UpdateUserSettings?accountId=${accountId}`, dbSettings, undefined, true, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

export const apiDownloadFileStream = async (downloadRequestPayload: IDownloadRequest) => {
    try {
        const fileBlob: Blob = (await axiosRetryPost("File/Download", downloadRequestPayload, {
            headers: {
                "Content-Type": "application/json"
            },
            responseType: "blob"
        }, true, localApiInstance))?.data;

        return fileBlob ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

// ---- HIX integration -------------------------------------------------------
// Companies and upload types are idempotent GETs and use the retrying helper.
export const apiHixGetCompanies = async (tenantId: string, accountId: string, name?: string, internalClientId?: string, page: number = 1) => {
    try {
        const results: IHixCompaniesResponse = (await axiosRetryGet("Hix/Companies", {
            params: { tenantId, accountId, name, internalClientId, page }
        }, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

export const apiHixGetUploadTypes = async (tenantId: string, accountId: string) => {
    try {
        const results: IHixUploadType[] = (await axiosRetryGet("Hix/UploadTypes", {
            params: { tenantId, accountId }
        }, localApiInstance))?.data;
        return results ?? [];
    } catch (error) {
        handleError(error);
    }
    return [];
}

// The upload is NOT routed through axiosRetryPost on purpose: HIX has no idempotency
// key, so a retried-but-already-succeeded POST would create a duplicate HIX task.
// retry-axios's default httpMethodsToRetry excludes POST, so a bare post() does not retry.
export const apiHixUploadTaskFiles = async (request: IHixUploadTaskFilesRequest) => {
    try {
        const results: IHixUploadTaskFilesResult = (await localApiInstance.post("Hix/UploadTaskFiles", request, {
            headers: { "Content-Type": "application/json" }
        }))?.data;
        return results ?? undefined;
    } catch (error) {
        // The backend returns a structured result (success=false + which file failed) on an
        // all-or-nothing abort or a HIX rejection; surface it so the panel can show the reason.
        const data = error?.response?.data;
        if (data && typeof data.success === "boolean") {
            return data as IHixUploadTaskFilesResult;
        }
        handleError(error);
    }
    return undefined;
}

export const apiCreateDocumentSet = async (documentSetCreationData: IDocumentSetCreationData): Promise<void> => {
    try {
        await axiosRetryPost("sharepoint/CreateDocumentSet", documentSetCreationData, undefined, true, localApiInstance);
    } catch (error) {
        handleError(error);
    }
}

export const apiGetCompressedPinnedData = async (accountId: string) => {
    try {
        const results: string = (await axiosRetryGet(`User/GetPinnedItems/${accountId}`, undefined, localApiInstance))?.data.value;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

export const apiGetOldPinnedItems = async (accountId: string) => {
    try {
        const results: IStoreItem[] = (await axiosRetryGet(`User/GetOldPinnedItems/${accountId}`, undefined, localApiInstance))?.data.value;
        return results ?? [];
    } catch (error) {
        handleError(error);
    }
    return [];
}

export const apiSetPinnedItems = async (accountId: string, updatedPinnedItems: IStoreItem[]): Promise<void> => {
    try {
        axiosRetryPost(`User/SetPinnedItems/${accountId}`,
            { encryptedJsonContent: LZString.compressToBase64(JSON.stringify(updatedPinnedItems)) }, undefined, true, localApiInstance);
    } catch (error) {
        handleError(error);
    }
}

interface SetSuggestedItemsRequest {
    accountId: string;
    senders: string[];
    conversationId: string;
    encryptedJsonContent?: string;
}

export const apiSetSuggestedItems = async (accountId: string, senders: string[], conversationId: string, updatedtSuggestedItems: IStoreItem[]): Promise<void> => {
    try {
        const request: SetSuggestedItemsRequest = {
            accountId,
            senders,
            conversationId,
            encryptedJsonContent: LZString.compressToBase64(JSON.stringify(updatedtSuggestedItems))
        }

        axiosRetryPost(`User/SetSuggestedSaveLocations`, request, undefined, true, localApiInstance);
    } catch (error) {
        handleError(error);
    }
}

export const apiGetCompressedSuggestedData = async (accountId: string, senders: string[], conversationId: string) => {
    try {
        const request: SetSuggestedItemsRequest = {
            accountId,
            senders,
            conversationId
        };
        const results = (await axiosRetryPost(`User/GetSuggestedSaveLocations`, request, undefined, true, localApiInstance))?.data.value;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

export const apiGetCompressedRecentData = async (accountId: string) => {
    try {
        const results: string = (await axiosRetryGet(`User/GetRecentItems/${accountId}`, undefined, localApiInstance))?.data.value;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

export const apiGetOldRecentItems = async (accountId: string) => {
    try {
        const results: IStoreItem[] = (await axiosRetryGet(`User/GetOldRecentItems/${accountId}`, undefined, localApiInstance))?.data.value;
        return results ?? [];
    } catch (error) {
        handleError(error);
    }
    return [];
}

export const apiSetRecentItems = async (accountId: string, updatedRecentItems: IStoreItem[]): Promise<void> => {
    try {
        axiosRetryPost(`User/SetRecentItems/${accountId}`,
            { encryptedJsonContent: LZString.compressToBase64(JSON.stringify(updatedRecentItems.filter(item => !item.cannotBeRemoved))) }, undefined, true, localApiInstance);
    } catch (error) {
        handleError(error);
    }
}

export const apiGetCompressedEmailToMetadataMapping = async (accountId: string) => {
    try {
        const results: string = (await axiosRetryGet(`User/GetEmailToMetadataMapping/${accountId}`, undefined, localApiInstance))?.data?.value;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

export const apiSetEmailToMetadataMapping = async (accountId: string, compressedData: string): Promise<void> => {
    try {
        await axiosRetryPost(`User/SetEmailToMetadataMapping/${accountId}`, { encryptedJsonContent: compressedData }, undefined, true, localApiInstance);
    } catch (error) {
        handleError(error);
    }
}

type DataToSend =
    | IFileSystemUploadData
    | ISharepointOfficeDataDto
    | IOneDriveOfficeDataDto
    | ISharepointDataDto
    | IOneDriveDataDto;

type UploadProgress = (event: { loaded: number; total?: number }) => void;

// An Office document's save posts a Blob-built JSON body (see jsonBlobBody). axios sends a Blob with
// the default POST Content-Type unless told otherwise, which the API's [FromBody] refuses.
const jsonBlobConfig = (body: unknown, onUploadProgress?: UploadProgress) =>
    body instanceof Blob ? { headers: { "Content-Type": "application/json" }, onUploadProgress } : undefined;

export const apiFileSystemCallDynamicEndpoint = async (
    endpoint: string,
    retryInternalServerError: boolean,
    dataToSend: DataToSend | Blob,
    onUploadProgress?: UploadProgress
): Promise<any> => {
    try {
        const results = (await axiosRetryPost(endpoint, dataToSend, jsonBlobConfig(dataToSend, onUploadProgress), retryInternalServerError, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        // A save failure must always propagate so the caller reports it to the user (see
        // logAndRethrow: handleError would swallow 401/403 and fake success).
        logAndRethrow(error, "ApiService.ts (apiFileSystemCallDynamicEndpoint)");
    }
}

export const apiConvertFileToPdf = async (requestPayload: IDriveItemRequest) => {
    try {
        const results: AxiosResponse = await axiosRetryPost("File/ConvertToPdf", requestPayload, {
            responseType: "blob"
        }, true, localApiInstance);

        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiGetAttachmentExtension = async (ewsUrl: string, ewsToken: string, attachmentIdList: string[]) => {
    try {
        const results = (await axiosRetryPost("sharepoint/GetAttachmentExtension", { ewsUrl, ewsToken, attachmentIdList }, undefined, false, localApiInstance))?.data;;

        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiFilePreCommitCheck = async (request: IFilePreCommitRequest) => {
    try {
        const results: IFilePreCommitResult = (await axiosRetryPost("sharepoint/FilePreCommitCheck", request, undefined, true, localApiInstance))?.data;

        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiFolderPreCommitCheck = async (request: FolderPreCommitRequest) => {
    try {
        const results = (await axiosRetryPost("sharepoint/FolderPreCommitCheck", request, undefined, true, localApiInstance))?.data;

        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiUpdateItemMetadata = async (request: MetadataUpdateRequest) => {
    try {
        const results = (await axiosRetryPost("sharepoint/UpdateItemMetadata", request, undefined, true, localApiInstance))?.data;

        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiBulkUpdateItemMetadata = async (request: BulkMetadataUpdateRequest) => {
    try {
        const results = (await axiosRetryPost("sharepoint/BulkUpdateItemMetadata", request, undefined, true, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

export const apiVerifyAndFireUpdateTrigger = async (request: UpdateTriggerRequest) => {
    try {
        const results = (await axiosRetryPost("sharepoint/VerifyAndFireUpdateTrigger", request, undefined, true, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

export const apiAddListItem = async (request: ListItemCreationData) => {
    try {
        const results = (await axiosRetryPost("sharepoint/AddListItem", request, undefined, true, localApiInstance))?.data;

        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiUploadExchangeMailFolder = async (request: ExchangeMailFolderUploadRequest) => {
    try {
        const results = (await axiosRetryPost("sharepoint/UploadExchangeMailFolder", request, undefined, true, localApiInstance))?.data;

        return results ?? undefined;
    } catch (error) {
        // Exchange-folder save is a data mutation like apiUpload: a swallowed 401/403 would
        // clear the user's selection and cached mappings as if the emails saved. Propagate.
        logAndRethrow(error, "ApiService.ts (apiUploadExchangeMailFolder)");
    }
}

// Token endpoints (account-bound JWT, docs/superpowers/specs/2026-10-13-copilot-chat-design.md
// §1.1). A 401 or 403 here means the Docubird JWT doesn't belong to the requested account, or
// carries no account (there is no grace window, D2). handleError would swallow both and leave
// AuthModule holding a state it can't refresh, so these six calls rethrow every failure and
// AuthModule decides: a 401/403 signs out (after one JWT renewal for a 401), and so does any other
// answer to a scheduled refresh, a sign-in Microsoft refused (interaction_required) and a 5xx
// included; no answer keeps it. A 401/403 or a refused sign-in is rethrown without logging: the API
// logged it. So is the failure of a call its caller no longer wants (stillWanted): it says nothing
// about the sign-in held now. And a try that ran out its time limit (2026-10-29, the user's choice):
// before 2026-10-26 such a call never ended, so it was never logged; in a long API hang each pane
// posted up to eight, to the API that hung.
// The trailing `return undefined;` stays because TypeScript doesn't treat a call to a `never`
// const as an exit (noImplicitReturns).
const rethrowTokenCallError = (error: any, context: string): never => {
    if (isAuthRejection(error) || isInteractionRequired(error) || error?.config?.stillWanted?.() === false || ranOutOfTime(error)) {
        throw error;
    }
    return logAndRethrow(error, context);
}

// Each try of a token call gets 5 minutes: a refresh takes 1-3 s, but one after Copilot consent took
// 55 s. Without a limit, a call that never answered held its refresh, and every caller waiting for
// it, for ever; before 2026-10-26 there was none, so a shorter limit would cut answers that still
// came. A try that ran out is not sent again: the server is still working on it (audit
// 2026-10-27). A scheduled refresh's call unanswered after 90 s is asked once more
// (AuthModule.askOnceMore).
// stillWanted: whether the caller still wants the answer; once it doesn't, the call isn't asked
// again (AxiosRetryUtil).
const tokenCallConfig = (stillWanted?: () => boolean): AxiosRequestConfig => ({ timeout: TOKEN_CALL_TIMEOUT_MS, retryTimedOut: false, stillWanted });

export const apiSilentTokenRequest = async (accountId: string, stillWanted?: () => boolean) => {
    try {
        const results: IAuthTokenResultDto = (await axiosRetryGet(`Auth/SilentTokenRequest?accountId=${accountId}`, tokenCallConfig(stillWanted), localApiInstance))?.data;

        return results ?? undefined;
    } catch (error) {
        rethrowTokenCallError(error, "ApiService.ts (apiSilentTokenRequest)");
    }

    return undefined;
}

export const apiRefreshTokenRequest = async (account: IAuthAccount, stillWanted?: () => boolean) => {
    try {
        const results: IAuthTokenResultDto = (await axiosRetryPost("Auth/RefreshTokenRequest", {
            accountId: account.accountId,
            tenantId: account.tenantId,
            sharePointBaseUrl: account.sharePointBaseUrl,
            oneDriveWebUrl: account.oneDriveWebUrl,
            displayName: account.displayName
        },
            tokenCallConfig(stillWanted), true, localApiInstance))?.data;

        return results ?? undefined;
    } catch (error) {
        rethrowTokenCallError(error, "ApiService.ts (apiRefreshTokenRequest)");
    }

    return undefined;
}

export const apiAquireFlowToken = async (authState: IAuthTokenResultDto, stillWanted?: () => boolean) => {
    try {
        const results: IAuthTokenResultDto = (await axiosRetryGet(`Auth/AquireFlowToken?accountId=${authState.account.accountId}`, tokenCallConfig(stillWanted), localApiInstance))?.data;

        return results ?? undefined;
    } catch (error) {
        rethrowTokenCallError(error, "ApiService.ts (apiAquireFlowToken)");
    }

    return undefined;
}

export const apiAquireSharePointAdminToken = async (authState: IAuthTokenResultDto, stillWanted?: () => boolean) => {
    try {
        let authUrl: URL = new URL(`${process.env.API_URL}Auth/AquireSharePointAdminToken`);
        authUrl.searchParams.append("accountId", authState.account?.accountId);
        authUrl.searchParams.append("sharePointBaseUrl", authState.account?.sharePointBaseUrl);

        const results: IAuthTokenResultDto = (await axiosRetryGet(authUrl.href, tokenCallConfig(stillWanted), localApiInstance))?.data;

        return results ?? undefined;
    } catch (error) {
        rethrowTokenCallError(error, "ApiService.ts (apiAquireSharePointAdminToken)");
    }

    return undefined;
}

export const apiAquireMailSendToken = async (authState: IAuthTokenResultDto) => {
    try {
        let authUrl: URL = new URL(`${process.env.API_URL}Auth/AquireMailSendToken`);
        authUrl.searchParams.append("accountId", authState.account?.accountId);

        const results: IAuthTokenResultDto = (await axiosRetryGet(authUrl.href, tokenCallConfig(), localApiInstance))?.data;

        return results ?? undefined;
    } catch (error) {
        rethrowTokenCallError(error, "ApiService.ts (apiAquireMailSendToken)");
    }

    return undefined;
}

export const apiAquireMailboxToken = async (authState: IAuthTokenResultDto) => {
    try {
        let authUrl: URL = new URL(`${process.env.API_URL}Auth/AquireMailboxToken`);
        authUrl.searchParams.append("accountId", authState.account?.accountId);

        const results: IAuthTokenResultDto = (await axiosRetryGet(authUrl.href, tokenCallConfig(), localApiInstance))?.data;

        return results ?? undefined;
    } catch (error) {
        rethrowTokenCallError(error, "ApiService.ts (apiAquireMailboxToken)");
    }

    return undefined;
}

// export const apiTeamsToken = async (authState: IAuthTokenResultDto) => {
//     try {
//         let authUrl: URL = new URL(`${process.env.API_URL}Auth/AquireTeamsToken`);
//         authUrl.searchParams.append("accountId", authState.account?.accountId);

//         const results: IAuthTokenResultDto = (await axiosRetryGet(authUrl.href, undefined, localApiInstance))?.data;

//         return results ?? undefined;
//     } catch (error) {
//         handleError(error);
//     }

//     return undefined;
// }

export const apiCheckIsTessAuthenticated = async (authState: IAuthTokenResultDto): Promise<boolean> => {
    try {
        let authUrl: URL = new URL(`${process.env.API_URL}Tess/CheckIsTessAuthenticated`);
        authUrl.searchParams.append("tenantId", authState.account?.tenantId);
        authUrl.searchParams.append("accountId", authState.account?.uniqueId);

        return (await axiosRetryGet(authUrl.href, undefined, localApiInstance))?.data === true;
    } catch (error) {
        handleError(error);
    }
    return false;
}

export const apiGetTessAccounts = async (accountEmailAddresses?: string[]): Promise<ITessAccountsResult> => {
    try {
        const authState = authModule.getPersistedAuthState();

        const requestUrl: URL = new URL(`${process.env.API_URL}Tess/GetAccounts`);
        requestUrl.searchParams.append("tenantId", authState.account?.tenantId);
        requestUrl.searchParams.append("accountId", authState.account?.uniqueId);

        const hasAccountEmailAddresses: boolean = Array.isArray(accountEmailAddresses) && accountEmailAddresses.length > 0;
        if (hasAccountEmailAddresses) {
            requestUrl.searchParams.append("email", accountEmailAddresses.map(x => x?.trim()).join(";"));
        }

        const results: ITessAccount[] = (await axiosRetryGet(requestUrl.href, undefined, localApiInstance))?.data;
        if (Array.isArray(results) && results.length > 0) {
            return { allowLoadAllAccounts: hasAccountEmailAddresses, accounts: results };
        }
        else if (hasAccountEmailAddresses) {
            // If no accounts were found for the sender address, load all accounts for signed in user
            return await apiGetTessAccounts();
        }
    } catch (error) {
        handleError(error);
    }
    return { allowLoadAllAccounts: false, accounts: [] };
}

export const apiGetTessProjects = async (accountId: string): Promise<ITessProjectFolders> => {
    try {
        const authState = authModule.getPersistedAuthState();

        const requestUrl: URL = new URL(`${process.env.API_URL}Tess/GetProjects`);
        requestUrl.searchParams.append("tenantId", authState.account?.tenantId);
        requestUrl.searchParams.append("accountId", authState.account?.uniqueId);
        requestUrl.searchParams.append("tessAccountId", accountId);

        const result: ITessProjectFolders = (await axiosRetryGet(requestUrl.href, undefined, localApiInstance))?.data;
        return result ?? null;
    } catch (error) {
        handleError(error);
    }
    return null;
}

export const apiPostTessEmail = async (requestPayload: ITessEmailRequest): Promise<ITessEmailTaskResponse> => {
    try {
        const authState = authModule.getPersistedAuthState();

        const requestUrl: URL = new URL(`${process.env.API_URL}Tess/Email`);
        requestUrl.searchParams.append("tenantId", authState.account?.tenantId);
        requestUrl.searchParams.append("accountId", authState.account?.uniqueId);

        const result: ITessEmailTaskResponse = (await axiosRetryPost(requestUrl.href, requestPayload, undefined, false, localApiInstance))?.data;
        return result ?? null;
    } catch (error) {
        handleError(error);
    }
    return null;
}

export const apiCreateFolders = async (accessToken: string, siteUrl: string, parentUrl: string, listId: string, autoFolderCreation: boolean, folders: IFileSystemFolder[]) => {
    try {
        const results: IFileSystemFolder[] = (await axiosRetryPost("sharepoint/CreateFolders", { accessToken, siteUrl, parentUrl, listId, autoFolderCreation, folders }, undefined, true, localApiInstance))?.data as IFileSystemFolder[];
        return results ?? []
    } catch (error) {
        handleError(error);
    }

    return []
}

export const apiUpload = async (fileUploadRequest: IFileUploadRequest, config?: AxiosRequestConfig) => {
    try {
        const uploadUrl: URL = new URL(`${process.env.API_URL}upload`);
        uploadUrl.searchParams.append("accountId", fileUploadRequest.accountId);

        if (fileUploadRequest.uploadId) {
            uploadUrl.searchParams.append("uploadId", fileUploadRequest.uploadId);
            uploadUrl.searchParams.append("chunkIndex", (fileUploadRequest.chunkIndex ?? 0).toString());
        }

        if (fileUploadRequest.path) {
            uploadUrl.searchParams.append("path", fileUploadRequest.path);
        }

        await axiosRetryPost(uploadUrl.href, fileUploadRequest.formData, config, true, localApiInstance);
    } catch (error) {
        // An upload failure must propagate so uploadDroppedFiles marks the file failed and the
        // save halts — handleError would swallow 401/403 and let the caller proceed to a save
        // that then fails with "Provided file content is empty".
        logAndRethrow(error, "ApiService.ts (apiUpload)");
    }
}

export const apiCompleteChunkUpload = async (completeUploadRequest: ICompleteFileUploadRequest) => {
    try {
        await axiosRetryPut("upload/complete", completeUploadRequest, localApiInstance);
    } catch (error) {
        // Same contract as apiUpload: a failed completion means the assembled file does
        // not exist server-side, so the failure must propagate to halt the save.
        logAndRethrow(error, "ApiService.ts (apiCompleteChunkUpload)");
    }
}

// Whether the error was posted: the logger counts repeats of an error it posted, and one that never
// arrived (signed out, offline, the API down) must not hold its next occurrence back.
export const apiClientError = async (clientError: any): Promise<boolean> => {
    // Log/ClientError requires the Docubird JWT. Signed out there is none: "Bearer undefined" got a
    // 401 for each error the sign-out itself caused (2026-10-26). The logger wrote it to the console.
    const jwt: string = authModule.getDocubirdTokenCached();
    if (!jwt) {
        return false;
    }
    try {
        // Use static axios instance as to not retry logging calls
        await axios.post(`${process.env.API_URL}Log/ClientError`, clientError, {
            headers: {
                Authorization: `Bearer ${jwt}`,
                [apiHeaders.key.xDocubirdAuth]: apiHeaders.value.xDocubirdAuth
            }
        });
        return true;
    } catch (error) {
        console.error(error);
        return false;
    }
}

export const apiLog = async (message: string, account: IAuthAccount, type?: string) => {
    try {
        await axiosRetryPost("Log", {
            message: `${message} - ${account?.username} - ${navigator?.userAgent}`,
            level: type
        }, undefined, true, localApiInstance);
    } catch (error) {
        handleError(error);
    }
}

export const apiSavePayload = async (mailPayloadDto: IMailSettingsDto) => {
    try {
        await axiosRetryPost("SaveOnSend/SavePayload", mailPayloadDto, undefined, true, localApiInstance);
    } catch (error) {
        handleError(error);
    }
}

export const apiGetPayload = async (accountId: string) => {
    try {
        const results: string = (await axiosRetryGet(`SaveOnSend/GetPayload/${accountId}`, undefined, localApiInstance))?.data
        return results ?? ""
    } catch (error) {
        handleError(error);
    }

    return "";
}

export const apiGetUserLicense = async (userAccount: IAuthAccount) => {
    try {
        const results: UserLicenseTypeEnum = (await axiosRetryPost("User/GetUserLicense", userAccount, undefined, true, localApiInstance))?.data;
        return results ?? UserLicenseTypeEnum.Expired
    } catch (error) {
        handleError(error);
    }

    return UserLicenseTypeEnum.Expired;
}

export const apiGetUserProfile = async (userAccount: IAuthAccount, appContext: string) => {
    try {
        const results: IUserProfile = (await axiosRetryPost(`User/GetUserProfile?appContext=${appContext}`, userAccount, undefined, true, localApiInstance))?.data;
        return results ?? null
    } catch (error) {
        handleError(error);
    }

    return null;
}

export const apiLoginAuthGetRequest = async (authUrlGetRequest: URL) => {
    try {
        // Use default axios retry instance to skip access token refresh interceptor
        const results = (await axiosRetryGet(
            authUrlGetRequest.href,
            {
                headers: {
                    [apiHeaders.key.xDocubirdAuth]: apiHeaders.value.xDocubirdAuth
                }
            }
        ))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiLoginCodeTokenRequest = async (codeTokenRequestUrl: URL) => {
    try {
        // Use default axios retry instance to skip access token refresh interceptor
        const results = (await axiosRetryGet(
            codeTokenRequestUrl.href,
            {
                headers: {
                    [apiHeaders.key.xDocubirdAuth]: apiHeaders.value.xDocubirdAuth
                }
            }
        ))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiGetBackgroundTaskProgess = async (taskId: string) => {
    try {
        const results = (await axiosRetryGet(`BackgroundTasks/progress/${taskId}`, undefined, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiFileReplace = async (request: ReplaceFileRequest | Blob, onUploadProgress?: UploadProgress) => {
    try {
        const results = (await axiosRetryPost("file/Replace", request, jsonBlobConfig(request, onUploadProgress), true, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        // As for a save: the panel reports a failure, and handleError would swallow 401/403 and fake success.
        logAndRethrow(error, "ApiService.ts (apiFileReplace)");
    }
}

export const apiFileCreateDocumentFromTemplate = async (request: CreateDocumentFromTemplateRequest) => {
    try {
        const results = (await axiosRetryPost("file/CreateDocumentFromTemplate", request, undefined, true, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

export const apiScanTemplateCustomFields = async (request: ScanTemplateCustomFieldsRequest) => {
    try {
        const results = (await axiosRetryPost("file/ScanTemplateCustomFields", request, undefined, true, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

// Deliberately NO auto-retry on any of the three TemplateAi calls: suggest runs a paid
// model call server-side (a gateway 502/504 while the model is still working would re-run
// it), apply MUTATES the template (a retry after a post-check-in 502 re-submits and
// surfaces a bogus version conflict), and each slide render is a download + rasterisation.
export const apiSuggestTemplatePlaceholders = async (request: import("../components/_v2/redux/api/local/api-local-interfaces").SuggestTemplatePlaceholdersRequest) => {
    try {
        const results = (await localApiInstance.post("file/SuggestTemplatePlaceholders", request))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

// Opens the panel on the document itself, before any suggestion is asked for. Catches like
// suggest above (a failure here must not kill the panel), and costs no AI quota.
export const apiPreviewTemplate = async (request: import("../components/_v2/redux/api/local/api-local-interfaces").PreviewTemplateRequest) => {
    try {
        const results = (await localApiInstance.post("file/PreviewTemplate", request))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

// Apply is STATELESS on the server: the full accepted proposals travel in the request and
// are re-validated there. 409 means version conflict or a checkout held by another user -
// surfaced to the caller rather than swallowed, because the panel renders it as guidance.
export const apiApplyTemplatePlaceholderProposals = async (request: import("../components/_v2/redux/api/local/api-local-interfaces").ApplyTemplatePlaceholderProposalsRequest) => {
    const results = (await localApiInstance.post("file/ApplyTemplatePlaceholderProposals", request))?.data;
    return results ?? undefined;
}

// No catch here either: the panel's SlidePreview needs the 409 (version conflict) to
// surface as a message instead of silently keeping a stale image.
export const apiRenderProposalSlide = async (request: import("../components/_v2/redux/api/local/api-local-interfaces").RenderProposalSlideRequest) => {
    const results = (await localApiInstance.post("file/RenderProposalSlide", request))?.data;
    return results ?? undefined;
}

export const apiFileConvertAndSaveAsPDF = async (request: ConvertAndSaveAsPDFRequest) => {
    try {
        const results = (await axiosRetryPost("file/ConvertAndSaveAsPDF", request, undefined, true, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiGetHooksByType = async (request: IHookRequest): Promise<IHookResult[]> => {
    try {
        const results = (await axiosRetryGet(`hooks?tenantGuid=${request.TenantGUID}&hookTypeId=${request.HookTypeId}`, undefined, localApiInstance))?.data;
        return results ?? []
    } catch (error) {
        handleError(error);
    }

    return [];
}

export const apiRequestApproval = async (request: ApprovalRequest) => {
    try {
        const results = (await axiosRetryPost("hooks/request/approval", request, undefined, true, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        return handleError(error);
    }
}

// export const apiRequestSigning = async (data: SignRequest) => {
//     try {
//         const results = (await axiosRetryPost("sign/create", data, undefined, true, localApiInstance))?.data;
//         return results ?? undefined;
//     } catch (error) {
//         handleError(error);
//     }
//     return undefined;
// }

export const apiGetAdditionalMailboxes = async (accountId: string) => {
    try {
        const results = (await axiosRetryGet(`user/GetAdditionalMailboxes/${accountId}`, undefined, localApiInstance))?.data;
        return results ?? []
    } catch (error) {
        handleError(error);
    }

    return [];
}

export const apiSetAdditionalMailboxes = async (accountId: string, additionalMailboxes: string[]) => {
    try {
        const results = (await axiosRetryPost(`user/SetAdditionalMailboxes/${accountId}`, additionalMailboxes, undefined, true, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

interface IRemoveProvisionedSitesRequest {
    accountId: string;
    siteIds: string[];
}

export const apiGetProvisionedSites = async (accountId: string) => {
    try {
        const results = (await axiosRetryGet(`Provisioning/GetProvisionedSites/${accountId}`, undefined, localApiInstance))?.data;
        return results ?? [];
    } catch (error) {
        handleError(error);
    }
    return [];
}

export const apiRemoveProvisionedSites = async (accountId: string, siteIdsToRemove: string[]) => {
    try {
        const data: IRemoveProvisionedSitesRequest = {
            accountId,
            siteIds: siteIdsToRemove
        }
        await axiosRetryPost("Provisioning/RemoveProvisionedSites", data, undefined, false, localApiInstance);
    } catch (error) {
        handleError(error);
    }
}

// GET api/Copilot/Prompts (spec 2026-10-13-copilot-chat-design.md §3.3). The tenant comes from the
// JWT's account claim, so nothing identifying is sent. Prompts are optional: any failure means
// none (handleError would rethrow everything but 401/403), and only the status is logged.
export const apiGetCopilotPrompts = async (): Promise<ICopilotPrompt[]> => {
    try {
        const results = (await axiosRetryGet("Copilot/Prompts", undefined, localApiInstance))?.data;
        return normalizeCopilotPrompts(results);
    } catch (error) {
        logger(new Error(`Copilot prompts could not be loaded (${error?.response?.status ?? "no response"})`), "ApiService.ts (apiGetCopilotPrompts)");
    }
    return [];
}